import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const ParsingQASettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [testingNotifications, setTestingNotifications] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    manual_review_enabled: false,
    awardwallet_fallback_enabled: false,
    flightaware_verification_enabled: false,
    parallel_parsing_enabled: false,
    sender_learning_enabled: false,
    confidence_threshold: 0.85,
    critical_fields: [
      'confirmation_number',
      'departure_date',
      'arrival_date',
      'departure_time',
      'arrival_time'
    ],
    auto_approve_graduated_senders: true,
    require_flightaware_for_flights: true,
    review_idle_timeout_seconds: 120,
    notifications: {
      enabled: false,
      beta_mode: true,
      email_enabled: false,
      email_primary_recipients: [],
      email_escalation_recipients: [],
      email_types: {
        immediate_alerts: true,
        daily_digest: true,
        escalation_alerts: true,
        weekly_report: false
      },
      email_digest_time: '08:00',
      email_timezone: 'America/New_York',
      slack_enabled: false,
      slack_webhook_url: '',
      slack_primary_channel: '#parsing-reviews',
      slack_escalation_channel: '',
      slack_types: {
        new_review_items: true,
        queue_status_updates: true,
        escalation_alerts: true,
        resolution_confirmations: true
      },
      max_immediate_per_hour: 10,
      quiet_hours_start: '22:00',
      quiet_hours_end: '06:00',
      escalation_threshold_hours: 4,
      queue_depth_threshold: 10
    },
    vip_organization_overrides: []
  });

  const [newRecipient, setNewRecipient] = useState('');
  const [newEscalationRecipient, setNewEscalationRecipient] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/parsing-qa/settings`, { headers });
      if (response.data.parsing_qa) {
        setSettings(prev => ({
          ...prev,
          ...response.data.parsing_qa,
          notifications: {
            ...prev.notifications,
            ...(response.data.parsing_qa.notifications || {})
          }
        }));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/parsing-qa/settings`, settings, { headers });
      setSuccessMessage('Settings saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const testNotifications = async (channel) => {
    setTestingNotifications(true);
    setError('');
    setSuccessMessage('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${apiBase}/admin/parsing-qa/notifications/test?channel=${channel}`,
        {},
        { headers }
      );
      setSuccessMessage(`Test notification sent! Results: ${JSON.stringify(response.data.results)}`);
    } catch (err) {
      setError('Failed to send test notification: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTestingNotifications(false);
    }
  };

  const addRecipient = (type) => {
    const email = type === 'primary' ? newRecipient : newEscalationRecipient;
    if (!email || !email.includes('@')) return;

    const key = type === 'primary' ? 'email_primary_recipients' : 'email_escalation_recipients';
    if (!settings.notifications[key].includes(email)) {
      setSettings(prev => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          [key]: [...prev.notifications[key], email]
        }
      }));
    }

    if (type === 'primary') {
      setNewRecipient('');
    } else {
      setNewEscalationRecipient('');
    }
  };

  const removeRecipient = (type, email) => {
    const key = type === 'primary' ? 'email_primary_recipients' : 'email_escalation_recipients';
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: prev.notifications[key].filter(e => e !== email)
      }
    }));
  };

  const Toggle = ({ checked, onChange, disabled }) => (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Access denied. Superadmin privileges required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parsing QA Settings</h1>
          <p className="text-gray-600">Configure the parsing quality assurance system</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-md font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {successMessage}
        </div>
      )}

      {/* Master Toggle */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Master Controls</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Enable Parsing QA System</h4>
              <p className="text-sm text-gray-500">
                Master toggle for the entire QA workflow. When disabled, all parsing goes through the standard pipeline.
              </p>
            </div>
            <Toggle
              checked={settings.enabled}
              onChange={(v) => setSettings(prev => ({ ...prev, enabled: v }))}
            />
          </div>

          {!settings.enabled && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                The QA system is currently disabled. Enable it to activate manual review and validation features.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Feature Toggles</h3>
          <p className="text-sm text-gray-500 mt-1">Enable individual QA features</p>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Manual Review Queue</h4>
              <p className="text-sm text-gray-500">Route failed parses to manual review</p>
            </div>
            <Toggle
              checked={settings.manual_review_enabled}
              onChange={(v) => setSettings(prev => ({ ...prev, manual_review_enabled: v }))}
              disabled={!settings.enabled}
            />
          </div>

          <div className="flex items-center justify-between border-t pt-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Sender Learning</h4>
              <p className="text-sm text-gray-500">Track accuracy by sender and auto-graduate reliable senders</p>
            </div>
            <Toggle
              checked={settings.sender_learning_enabled}
              onChange={(v) => setSettings(prev => ({ ...prev, sender_learning_enabled: v }))}
              disabled={!settings.enabled}
            />
          </div>

          <div className="flex items-center justify-between border-t pt-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900">FlightAware Verification</h4>
              <p className="text-sm text-gray-500">Verify parsed flights against FlightAware schedules</p>
            </div>
            <Toggle
              checked={settings.flightaware_verification_enabled}
              onChange={(v) => setSettings(prev => ({ ...prev, flightaware_verification_enabled: v }))}
              disabled={!settings.enabled}
            />
          </div>

          <div className="flex items-center justify-between border-t pt-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900">AwardWallet Fallback</h4>
              <p className="text-sm text-gray-500">Use AwardWallet as parsing fallback (requires credentials)</p>
            </div>
            <Toggle
              checked={settings.awardwallet_fallback_enabled}
              onChange={(v) => setSettings(prev => ({ ...prev, awardwallet_fallback_enabled: v }))}
              disabled={!settings.enabled}
            />
          </div>

          <div className="flex items-center justify-between border-t pt-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Parallel Parsing</h4>
              <p className="text-sm text-gray-500">Run AI and AwardWallet in parallel for comparison</p>
            </div>
            <Toggle
              checked={settings.parallel_parsing_enabled}
              onChange={(v) => setSettings(prev => ({ ...prev, parallel_parsing_enabled: v }))}
              disabled={!settings.enabled || !settings.awardwallet_fallback_enabled}
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
          <p className="text-sm text-gray-500 mt-1">Configure alerts for the review team</p>
        </div>
        <div className="p-6 space-y-6">
          {/* Master notification toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Enable Notifications</h4>
              <p className="text-sm text-gray-500">Send alerts when items need review</p>
            </div>
            <Toggle
              checked={settings.notifications.enabled}
              onChange={(v) => setSettings(prev => ({
                ...prev,
                notifications: { ...prev.notifications, enabled: v }
              }))}
              disabled={!settings.enabled}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Beta Mode</h4>
              <p className="text-sm text-gray-500">Disable throttling and quiet hours (all notifications immediate)</p>
            </div>
            <Toggle
              checked={settings.notifications.beta_mode}
              onChange={(v) => setSettings(prev => ({
                ...prev,
                notifications: { ...prev.notifications, beta_mode: v }
              }))}
              disabled={!settings.notifications.enabled}
            />
          </div>

          {/* Email Settings */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">📧</span>
                <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>
              </div>
              <Toggle
                checked={settings.notifications.email_enabled}
                onChange={(v) => setSettings(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, email_enabled: v }
                }))}
                disabled={!settings.notifications.enabled}
              />
            </div>

            {settings.notifications.email_enabled && (
              <div className="ml-7 space-y-4">
                {/* Primary Recipients */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Recipients
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && addRecipient('primary')}
                    />
                    <button
                      onClick={() => addRecipient('primary')}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {settings.notifications.email_primary_recipients.map(email => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm"
                      >
                        {email}
                        <button
                          onClick={() => removeRecipient('primary', email)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Escalation Recipients */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Escalation Recipients
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={newEscalationRecipient}
                      onChange={(e) => setNewEscalationRecipient(e.target.value)}
                      placeholder="manager@example.com"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && addRecipient('escalation')}
                    />
                    <button
                      onClick={() => addRecipient('escalation')}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {settings.notifications.email_escalation_recipients.map(email => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm"
                      >
                        {email}
                        <button
                          onClick={() => removeRecipient('escalation', email)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Test Email */}
                <button
                  onClick={() => testNotifications('email')}
                  disabled={testingNotifications || settings.notifications.email_primary_recipients.length === 0}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 disabled:opacity-50"
                >
                  {testingNotifications ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>
            )}
          </div>

          {/* Slack Settings */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">💬</span>
                <h4 className="text-sm font-medium text-gray-900">Slack Notifications</h4>
              </div>
              <Toggle
                checked={settings.notifications.slack_enabled}
                onChange={(v) => setSettings(prev => ({
                  ...prev,
                  notifications: { ...prev.notifications, slack_enabled: v }
                }))}
                disabled={!settings.notifications.enabled}
              />
            </div>

            {settings.notifications.slack_enabled && (
              <div className="ml-7 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL
                  </label>
                  <input
                    type="password"
                    value={settings.notifications.slack_webhook_url}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, slack_webhook_url: e.target.value }
                    }))}
                    placeholder="https://hooks.slack.com/services/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Channel
                    </label>
                    <input
                      type="text"
                      value={settings.notifications.slack_primary_channel}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, slack_primary_channel: e.target.value }
                      }))}
                      placeholder="#parsing-reviews"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Escalation Channel (optional)
                    </label>
                    <input
                      type="text"
                      value={settings.notifications.slack_escalation_channel}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, slack_escalation_channel: e.target.value }
                      }))}
                      placeholder="#parsing-alerts"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Test Slack */}
                <button
                  onClick={() => testNotifications('slack')}
                  disabled={testingNotifications || !settings.notifications.slack_webhook_url}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 disabled:opacity-50"
                >
                  {testingNotifications ? 'Sending...' : 'Test Slack Connection'}
                </button>
              </div>
            )}
          </div>

          {/* Throttling Settings */}
          {!settings.notifications.beta_mode && (
            <div className="border-t pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Throttling Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max notifications per hour
                  </label>
                  <input
                    type="number"
                    value={settings.notifications.max_immediate_per_hour}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, max_immediate_per_hour: parseInt(e.target.value) || 10 }
                    }))}
                    min="1"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escalation threshold (hours)
                  </label>
                  <input
                    type="number"
                    value={settings.notifications.escalation_threshold_hours}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, escalation_threshold_hours: parseInt(e.target.value) || 4 }
                    }))}
                    min="1"
                    max="24"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quiet hours start
                  </label>
                  <input
                    type="time"
                    value={settings.notifications.quiet_hours_start}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, quiet_hours_start: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quiet hours end
                  </label>
                  <input
                    type="time"
                    value={settings.notifications.quiet_hours_end}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, quiet_hours_end: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Validation Thresholds */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Validation Thresholds</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confidence Threshold
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Items below this confidence score will be sent to manual review
            </p>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={settings.confidence_threshold}
              onChange={(e) => setSettings(prev => ({ ...prev, confidence_threshold: parseFloat(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>50%</span>
              <span className="font-medium text-gray-900">{(settings.confidence_threshold * 100).toFixed(0)}%</span>
              <span>100%</span>
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Auto-approve Graduated Senders</h4>
              <p className="text-sm text-gray-500">Skip manual review for senders with proven accuracy</p>
            </div>
            <Toggle
              checked={settings.auto_approve_graduated_senders}
              onChange={(v) => setSettings(prev => ({ ...prev, auto_approve_graduated_senders: v }))}
              disabled={!settings.sender_learning_enabled}
            />
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Require FlightAware for Flights</h4>
              <p className="text-sm text-gray-500">Always verify flight data before auto-approval</p>
            </div>
            <Toggle
              checked={settings.require_flightaware_for_flights}
              onChange={(v) => setSettings(prev => ({ ...prev, require_flightaware_for_flights: v }))}
              disabled={!settings.flightaware_verification_enabled}
            />
          </div>
        </div>
      </div>

      {/* Review Queue Settings */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Review Queue Settings</h3>
          <p className="text-sm text-gray-500 mt-1">Configure reviewer assignment and idle behavior</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reviewer Idle Timeout
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Automatically unassign a review item if the reviewer is idle (no mouse or keyboard activity) for this duration. Set to 0 to disable.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                max="3600"
                step="30"
                value={settings.review_idle_timeout_seconds}
                onChange={(e) => setSettings(prev => ({ ...prev, review_idle_timeout_seconds: parseInt(e.target.value) || 0 }))}
                className="w-28 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.enabled}
              />
              <span className="text-sm text-gray-500">seconds</span>
              <span className="text-sm text-gray-400">
                ({settings.review_idle_timeout_seconds >= 60
                  ? `${Math.floor(settings.review_idle_timeout_seconds / 60)}m ${settings.review_idle_timeout_seconds % 60 ? `${settings.review_idle_timeout_seconds % 60}s` : ''}`
                  : `${settings.review_idle_timeout_seconds}s`})
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParsingQASettings;
